/**
 * Client de taxonomie — le référentiel servi par le backend, côté admin.
 *
 * Chantier « ID de compétence », phase P3.
 * Documentation/IMPLEMENTATION/CARTOGRAPHIE_IMPACT_ID_COMPETENCES.md
 *
 * ─── Pourquoi ce module existe ──────────────────────────────────────────────
 * admin-web lisait la taxonomie dans `_shared_base.js`, une copie du fichier
 * frontend régénérée au build. Tant que la taxonomie ne changeait qu'en éditant
 * du code, cela suffisait. Dès lors qu'elle devient éditable en base, une copie
 * figée au build serait périmée dès la première modification — et l'admin est
 * précisément l'endroit d'où on la modifie.
 *
 * ─── Deux propriétés à tenir ────────────────────────────────────────────────
 *
 * 1. API SYNCHRONE. Les 16 écrans appellent `getAllCompetences()` en plein
 *    rendu React. Les rendre asynchrones supposerait de tous les réécrire ; on
 *    hydrate donc AVANT le rendu (App.jsx, après l'auth) et on lit un cache.
 *
 * 2. REPLI SUR `_shared_base`. Tant que l'hydratation n'a pas eu lieu — ou si
 *    elle échoue — les fonctions rendent ce que rendait le code d'avant. Un
 *    back-office ne doit pas se retrouver vide parce qu'un appel a échoué ; et
 *    le repli est exact, la parité entre les deux sources étant vérifiée par
 *    test côté backend.
 */

import shared from './_shared_base.js';
import { apiRequest } from '../utils/axios-config';

// ─────────────────────────── état ───────────────────────────

/**
 * Instantané servi par le backend. `null` = pas encore hydraté → repli.
 * @type {{version: number, domains: any[], competences: any[], notions: any[], aliases: any[], gameFit: any[]}|null}
 */
let referentiel = null;

/** Index dérivés, reconstruits à chaque hydratation. */
let index = null;

const CLE_CACHE = 'dicto.admin.taxonomy';

function construireIndex(snapshot) {
  const domainesParId = new Map(snapshot.domains.map((d) => [d.id, d]));
  const competencesParId = new Map(snapshot.competences.map((c) => [c.id, c]));
  const notionsParId = new Map(snapshot.notions.map((n) => [n.id, n]));

  const aliasNotion = new Map();
  const aliasCompetence = new Map();
  for (const a of snapshot.aliases || []) {
    if (a.scope === 'notion') aliasNotion.set(a.alias, a.targetId);
    if (a.scope === 'competence') aliasCompetence.set(a.alias, a.targetId);
  }

  const aptitudes = new Map();
  for (const g of snapshot.gameFit || []) {
    aptitudes.set(`${g.competenceId}::${g.gameType}`, g);
  }

  return { domainesParId, competencesParId, notionsParId, aliasNotion, aliasCompetence, aptitudes };
}

function activer(snapshot) {
  referentiel = snapshot;
  index = construireIndex(snapshot);
}

// ─────────────────────────── hydratation ───────────────────────────

/**
 * Récupère le référentiel et le met en cache.
 *
 * À appeler UNE FOIS au démarrage, après l'authentification (l'endpoint la
 * requiert) et avant le rendu des écrans. Ne jette jamais : en cas d'échec, le
 * repli reste actif et l'incident est journalisé.
 *
 * @returns {Promise<{source: 'backend'|'cache'|'repli', version: number|null}>}
 */
export async function hydraterTaxonomie() {
  // Cache local d'abord : l'admin reste utilisable même hors ligne, et le rendu
  // ne dépend pas de la latence de l'appel.
  try {
    const brut = localStorage.getItem(CLE_CACHE);
    if (brut) activer(JSON.parse(brut));
  } catch {
    // Cache illisible (format changé, quota) : sans conséquence, on refetch.
    localStorage.removeItem(CLE_CACHE);
  }

  try {
    const payload = await apiRequest('get', '/taxonomy');
    if (!payload?.notions?.length) throw new Error('référentiel vide');

    activer(payload);
    try {
      localStorage.setItem(CLE_CACHE, JSON.stringify(payload));
    } catch {
      // Quota dépassé : le cache mémoire suffit pour cette session.
    }
    return { source: 'backend', version: payload.version };
  } catch (error) {
    if (referentiel) {
      console.warn('[taxonomy] Backend injoignable — cache local conservé.', error?.message);
      return { source: 'cache', version: referentiel.version };
    }
    console.warn('[taxonomy] Backend injoignable — repli sur la copie embarquée.', error?.message);
    return { source: 'repli', version: null };
  }
}

/** Force un rechargement (après édition du référentiel). */
export async function rafraichirTaxonomie() {
  localStorage.removeItem(CLE_CACHE);
  referentiel = null;
  index = null;
  return hydraterTaxonomie();
}

/** Provenance courante — affichée dans l'UI pour lever toute ambiguïté. */
export function getTaxonomySource() {
  if (!referentiel) return { source: 'repli', version: null };
  return { source: 'backend', version: referentiel.version };
}

// ─────────────────────── helpers de projection ───────────────────────

/**
 * Clé d'arbre / sélection — `id` stable (aligné store frontend post-P5, admin P6).
 *
 * Les filtres admin et les jointures BDD ciblent l'ID. La résolution de libellés
 * historiques passe par `aliases[]` (`getNotionLabel`, NLU).
 */
function cle(entree) {
  return entree?.id || '';
}

/** Clés historiques (« grammaire », « types_phrases ») d'une notion du référentiel. */
function clesDe(notion) {
  const competence = index.competencesParId.get(notion.competenceId);
  if (!competence) return null;
  const domaine = index.domainesParId.get(competence.domainId);
  if (!domaine) return null;
  return { domain: cle(domaine), competence: cle(competence), domaineRef: domaine, competenceRef: competence };
}

/**
 * Notions triées comme le fait `getCompetencesforLevel` : par niveau
 * d'introduction croissant, puis par `ordre`. L'ordre est du comportement —
 * des écrans prennent le premier élément d'une liste.
 */
function notionsOrdreCanonique() {
  const rangNiveau = (niveau) => shared.ordreNiveaux.indexOf(niveau);
  return [...referentiel.notions].sort((a, b) => {
    const ecart = rangNiveau(a.introducedAtLevel) - rangNiveau(b.introducedAtLevel);
    return ecart !== 0 ? ecart : a.ordre - b.ordre;
  });
}

// ─────────────────────────── lecture ───────────────────────────

/**
 * Liste plate `{ domain, competence, notion, level, progression }`.
 * Format inchangé : c'est celui qu'attendent les 14 appels des écrans.
 */
export function getAllCompetences() {
  if (!referentiel) return [];

  return notionsOrdreCanonique().map((notion) => {
    const cles = clesDe(notion);
    return {
      domain: cles?.domain ?? '',
      competence: cles?.competence ?? '',
      // ⚠️ La CLÉ, pas le libellé : les filtres admin interrogent la base en
      // égalité stricte sur `parameters->competence->>notion`. Servir `label`
      // ici ferait qu'un renommage vide toutes les listes d'exercices, sans
      // erreur. Voir §10.1 P4.
      notion: cle(notion),
      level: notion.introducedAtLevel,
      // Chaque notion n'est déclarée qu'à son niveau d'introduction ; la
      // « progression » se réduit donc à ce niveau. Conservé sous forme de
      // tableau pour ne rien changer aux appelants.
      progression: [notion.introducedAtLevel],
    };
  });
}

/** Arborescence `{ domain: { competence: [notions] } }`, tri alphabétique. */
export function getAllAvailableNotions() {
  if (!referentiel) return {};

  const resultat = {};
  for (const domaine of [...referentiel.domains].sort((a, b) => a.ordre - b.ordre)) {
    const competences = referentiel.competences
      .filter((c) => c.domainId === domaine.id)
      .sort((a, b) => a.ordre - b.ordre);

    for (const competence of competences) {
      // Clés, pas libellés (cf. getAllCompetences).
      const notions = referentiel.notions
        .filter((n) => n.competenceId === competence.id)
        .map((n) => cle(n))
        .sort();
      if (!notions.length) continue;
      if (!resultat[cle(domaine)]) resultat[cle(domaine)] = {};
      resultat[cle(domaine)][cle(competence)] = notions;
    }
  }
  return resultat;
}

/** Notions disponibles jusqu'à un niveau (cumulatif), ordre canonique. */
export function getCompetencesforLevel(niveauOuCycle) {
  if (!referentiel) return {};

  const niveau =
    typeof niveauOuCycle === 'number' || /^\d$/.test(String(niveauOuCycle))
      ? shared.cycleCeilingLevel(Number(niveauOuCycle))
      : niveauOuCycle;

  const plafond = shared.ordreNiveaux.indexOf(niveau);
  if (plafond < 0) return {};

  const retenues = notionsOrdreCanonique().filter(
    (n) => shared.ordreNiveaux.indexOf(n.introducedAtLevel) <= plafond
  );

  const resultat = {};
  for (const domaine of [...referentiel.domains].sort((a, b) => a.ordre - b.ordre)) {
    const competences = referentiel.competences
      .filter((c) => c.domainId === domaine.id)
      .sort((a, b) => a.ordre - b.ordre);

    for (const competence of competences) {
      // Clés, pas libellés (cf. getAllCompetences).
      const notions = retenues.filter((n) => n.competenceId === competence.id).map((n) => cle(n));
      if (!notions.length) continue;
      if (!resultat[cle(domaine)]) resultat[cle(domaine)] = {};
      resultat[cle(domaine)][cle(competence)] = notions;
    }
  }
  return resultat;
}

/** Libellé d'affichage d'une notion (accepte un ID, un libellé courant ou legacy). */
export function getNotionLabel(reference) {
  if (!referentiel) return reference ? String(reference) : '';
  if (!reference) return '';

  const valeur = String(reference).trim();
  const id = index.notionsParId.has(valeur) ? valeur : index.aliasNotion.get(valeur);
  return id ? index.notionsParId.get(id).label : (reference ? String(reference) : '');
}

/** Libellé d'affichage d'une compétence. */
export function getCompetenceName(domain, competence) {
  if (!referentiel) return competence || '';

  const id = index.competencesParId.has(competence)
    ? competence
    : index.aliasCompetence.get(`${domain}.${competence}`) || index.aliasCompetence.get(competence);

  return id ? index.competencesParId.get(id).label : (competence || '');
}

/** Libellés des domaines, format `{ id: label }`. */
export function getDomainNames() {
  if (!referentiel) return {};

  // Tri par `ordreAffichage` (celui des menus) et NON par `ordre` (progression
  // scolaire) : le CP ne porte que du vocabulaire, si bien que l'ordre
  // pédagogique placerait « Vocabulaire » en tête de tous les menus.
  return Object.fromEntries(
    [...referentiel.domains]
      .sort((a, b) => (a.ordreAffichage ?? a.ordre) - (b.ordreAffichage ?? b.ordre))
      .map((d) => [cle(d), d.label])
  );
}

/** Libellés des compétences, format `{ domain: { competence: label } }`. */
export function getCompetenceNames() {
  if (!referentiel) return {};

  const resultat = {};
  for (const competence of referentiel.competences) {
    const domaine = index.domainesParId.get(competence.domainId);
    if (!domaine) continue;
    if (!resultat[cle(domaine)]) resultat[cle(domaine)] = {};
    resultat[cle(domaine)][cle(competence)] = competence.label;
  }
  return resultat;
}

/** Profil d'une compétence (minUnit, affordedModes, angle). */
export function getNotionProfile() {
  if (!referentiel) return {};

  const resultat = {};
  for (const competence of referentiel.competences) {
    const domaine = index.domainesParId.get(competence.domainId);
    if (!domaine) continue;
    resultat[`${cle(domaine)}.${cle(competence)}`] = competence.profile || {};
  }
  return resultat;
}

/**
 * Aptitude d'un type de jeu pour une compétence.
 * @returns {{verdict: 'fit'|'unfit'}|null} null = rien de déclaré.
 */
export function getGameFit(domain, competence, gameType) {
  if (!referentiel) return null;
  const regle = index.aptitudes.get(`${competence}::${gameType}`);
  return regle ? { verdict: regle.verdict } : null;
}

/**
 * `association` est-elle pertinente pour cette compétence ?
 * Registre d'AUTORISATION : sans déclaration explicite, la réponse est non.
 */
export function isAssociationFitForCompetence(domain, competence) {
  if (!referentiel) return false;
  return getGameFit(domain, competence, 'association')?.verdict === 'fit';
}

/** Évaluation détaillée de l'aptitude à `association` (message d'UI compris). */
export function getAssociationFitAssessment(domain, competence, notion) {
  if (!referentiel) return { fit: false, reason: 'Référentiel non hydraté.', alternatives: [], notion };
  const fit = getGameFit(domain, competence, 'association')?.verdict === 'fit';
  return {
    fit,
    reason: fit
      ? null
      : 'L’association n’est pas éligible pour cette compétence (référentiel).',
    alternatives: ['speed_sort', 'speed_game'],
    notion,
  };
}


/** Thèmes (vocabulaire) / leçons — ex-shared_config.getGameCompetences. */
export function getGameCompetences(niveauOuCycle) {
  const byDomain = getCompetencesforLevel(niveauOuCycle) || {};
  const themes = {};
  const lessons = {};
  Object.entries(byDomain).forEach(([domain, comps]) => {
    Object.entries(comps || {}).forEach(([competence, notions]) => {
      (notions || []).forEach((notion) => {
        const info = {
          domain,
          competence,
          notion,
          label: getNotionLabel(notion),
          competenceLabel: getCompetenceName(domain, competence),
        };
        if (domain === 'vocabulaire') themes[notion] = info;
        else lessons[notion] = info;
      });
    });
  });
  return { themes, lessons };
}
export function getThemes(level) {
  return Object.values(getGameCompetences(level).themes);
}
export function getLessons(level) {
  return Object.values(getGameCompetences(level).lessons);
}
